import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAnnouncement } from '../../api/Index';
import './CreateNotice.css';
import PageTitle from '../../components/UI/PageTitle';

const CreateNotice = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    importance: 'medium',
    expiryDate: ''
  });
  
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleImportanceChange = (importance) => {
    setFormData({
      ...formData,
      importance
    });
    setError(null);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    
    if (!formData.content.trim()) {
      setError('Notice content is required');
      return false;
    }
    
    if (formData.expiryDate) {
      const selectedDate = new Date(formData.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        setError('Expiry date cannot be in the past');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    
    setCreating(true);
    try {
      // Prepare data for API
      const noticeData = {
        title: formData.title,
        content: formData.content,
        importance: formData.importance,
        ...(formData.expiryDate && { expiryDate: formData.expiryDate })
      };
      
      const response = await createAnnouncement(noticeData);
      
      // Redirect to notices page
      navigate('/notices');
      
    } catch (err) {
      console.error('Error creating notice:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to create notice');
      setCreating(false);
    }
  };
  
  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'low':
        return '#28a745';
      case 'high':
        return '#dc3545';
      case 'medium':
      default:
        return '#ffc107';
    }
  };
  
  return (
    <div>
        <PageTitle title='Create Notice' />
        <div className="create-notice-container">
            <div className="form-container">
                {error && (
                    <div style={{
                        backgroundColor: '#ffe6e6',
                        border: '1px solid #ff0000',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '20px',
                        color: '#cc0000'
                    }}>
                        {error}
                    </div>
                )}
                
                
                <form className="create-notice-form" onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label className="input-label">Title *</label>
                        <input
                            type="text"
                            name="title"
                            className="input"
                            placeholder="Enter Notice Title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Notice *</label>
                        <textarea
                            name="content"
                            className="text-area"
                            placeholder="Enter Notice"
                            value={formData.content}
                            onChange={handleInputChange}
                            required
                            rows={6}
                        />
                    </div>

                    <div className='importance-group'>
                        <label className="input-label">Importance</label>
                        <div className='importance-label-group'>
                            <button
                                type="button"
                                onClick={() => handleImportanceChange('low')}
                                style={{
                                    backgroundColor: formData.importance === 'low' ? getImportanceColor('low') : '#f8f9fa',
                                    color: formData.importance === 'low' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Low
                            </button>
                            <button
                                type="button"
                                onClick={() => handleImportanceChange('medium')}
                                style={{
                                    backgroundColor: formData.importance === 'medium' ? getImportanceColor('medium') : '#f8f9fa',
                                    color: formData.importance === 'medium' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Medium
                            </button>
                            <button
                                type="button"
                                onClick={() => handleImportanceChange('high')}
                                style={{
                                    backgroundColor: formData.importance === 'high' ? getImportanceColor('high') : '#f8f9fa',
                                    color: formData.importance === 'high' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                High
                            </button>
                        </div>
                    </div>

                    <div className='input-group'>
                        <label className="input-label">Expiry Date</label>
                        <input
                            type="date"
                            name="expiryDate"
                            className="date-input"
                            value={formData.expiryDate}
                            onChange={handleInputChange}
                            min={new Date().toISOString().split('T')[0]}
                        />
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                            Leave empty for notices that don't expire
                        </small>
                    </div>

                    <button 
                        type="submit"
                        className='form-button'
                        disabled={creating}
                        style={{
                            opacity: creating ? 0.6 : 1,
                            cursor: creating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {creating ? 'Creating Notice...' : 'Create Notice'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default CreateNotice;